import { useState } from "react";
import api from "@/lib/api";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { extractApiError } from "@/components/PanelState";

// CC-301: inline call tracking (Called, date, outcome, notes, last updated). Saves without reload.
export const CALL_OUTCOMES = ["Reached", "Voicemail", "No answer", "Wrong number", "Not interested", "Interview set"];

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export function CallTrackingEditor({ trackKey, initial, onSaved, testId = "call-tracking" }) {
  const [v, setV] = useState({
    called: !!initial?.called, called_on: initial?.called_on || "", outcome: initial?.outcome || "", notes: initial?.notes || "",
  });
  const [last, setLast] = useState(initial || null);
  const [state, setState] = useState({ saving: false, error: null, saved: false });
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function save() {
    setState({ saving: true, error: null, saved: false });
    try {
      const { data } = await api.put("/hiring/call-tracking", {
        key: trackKey, called: v.called, called_on: v.called_on || null, outcome: v.outcome || null, notes: v.notes,
      });
      setLast(data.call_tracking);
      setState({ saving: false, error: null, saved: true });
      onSaved?.(data.call_tracking);
    } catch (e) {
      setState({ saving: false, error: extractApiError(e), saved: false });
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-2 text-xs" data-testid={testId}>
      <label className="flex items-center gap-1 pt-1.5">
        <input type="checkbox" checked={v.called} onChange={set("called")} data-testid={`${testId}-called`} /> Called
      </label>
      <input type="date" value={v.called_on} onChange={set("called_on")} className="border border-[#d1d5db] rounded px-1.5 py-1"
        data-testid={`${testId}-date`} aria-label="Date called" />
      <select value={v.outcome} onChange={set("outcome")} className="border border-[#d1d5db] rounded px-1.5 py-1" data-testid={`${testId}-outcome`}
        aria-label="Outcome">
        <option value="">Outcome…</option>
        {CALL_OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <textarea value={v.notes} onChange={set("notes")} rows={1} placeholder="Notes" className="border border-[#d1d5db] rounded px-1.5 py-1 min-w-[180px] flex-1"
        data-testid={`${testId}-notes`} />
      <Button size="sm" variant="outline" onClick={save} disabled={state.saving} data-testid={`${testId}-save`}>
        {state.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        <span className="ml-1">Save</span>
      </Button>
      <div className="basis-full text-[11px] text-[#6b7280]" data-testid={`${testId}-last-updated`}>
        {state.error ? <span className="text-[#991b1b]">{state.error.message || "Save failed."}</span>
          : last?.updated_at ? <>Last updated by {last.updated_by_name || last.updated_by} · {fmtWhen(last.updated_at)}{state.saved ? " · Saved" : ""}</>
          : "Not tracked yet"}
      </div>
    </div>
  );
}
